import { useIntl } from "@/i18n/i18n-client";
import Modal from "@/shared/globals/UiElements/Modal";
import React, { useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useStore } from "@/lib/storeData/StoreContext";
import { getButtonColorsOnType } from "@/templates/modern/components/Button/ButtonPalette";
import { theme } from "@/shared/theme";
import { isBrowser } from "framer-motion";
import {
  CustomerServiceErrorCodes,
  DefaultPhoneMethod,
  useSendCustomerOtpMutation,
  VerificationMethod,
} from "@/generated/graphql";
import { verifyOTP, signup } from "@/lib/Authentication/services/authService";
import { useAuth } from "@/lib/Authentication/hooks/useAuth";

// Import new components
import LoginSwitcher from "@/templates/modern/components/Authentication/LoginSwitcher";
import { LoginMethod } from "@/templates/types/LoginFormProps";
import LoginForm from "./components/LoginForm";
import OtpVerification from "./components/OtpVerification";
import SignupForm from "./components/SignupForm";
import ModalHeader, { ModalStep } from "./components/ModalHeader";

import ActionButtons from "./components/ActionButtons";
import { FeaturedOutlineIcon } from "@/assets/Icons/FeaturedOutlineIcon";
import { handleLoginUpdate } from "@/lib/Authentication/utils/loginHelpers";
import {
  getStoreAuthErrorMessageString,
  getCustomerErrorMessageString,
} from "@/shared/utils/error-utils";
import { useCartData } from "@/lib/cartData/useCartData";
import { parseCookies } from "nookies";

// Re-export for backward compatibility
export { LoginMethod as loginState };
export { ModalStep as ModalStep };

export const decodeToken = (token: string) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/"); // JWT uses base64url encoding
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};
interface CentralizedModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete?: () => void;
  onAuthSuccess?: () => void;
  centralizedModalStep?: ModalStep;
  notVerifiedEmail?: string | null;
  notVerifiedPhone?: string | null;
}

const CentralizedModal = ({
  isOpen,
  onClose,
  onAuthSuccess,
  centralizedModalStep,
  notVerifiedEmail,
  notVerifiedPhone,
}: CentralizedModalProps) => {
  const { appearance, storeId, defaultPhoneMethod, verificationMethods } =
    useStore();
  const { updateUser } = useAuth();
  const { updateContactInfo: updateContactInfoObj } = useCartData();
  const { updateContactInfo } = updateContactInfoObj;

  // Check available verification methods
  const hasEmailMethod =
    verificationMethods?.includes(VerificationMethod.Email) ?? true;
  const hasPhoneMethod =
    (verificationMethods?.includes(VerificationMethod.Sms) ||
      verificationMethods?.includes(VerificationMethod.Whatsapp)) ??
    true;

  // Set initial method based on available verification methods
  const getInitialMethod = useCallback(() => {
    if (hasEmailMethod && hasPhoneMethod) {
      return LoginMethod.EMAIL; // Default to email if both are available
    }
    if (hasEmailMethod) {
      return LoginMethod.EMAIL;
    }
    if (hasPhoneMethod) {
      return LoginMethod.PHONE;
    }
    return LoginMethod.EMAIL; // Fallback
  }, [hasEmailMethod, hasPhoneMethod]);

  const [method, setMethod] = useState<LoginMethod>(getInitialMethod());
  const [step, setStep] = useState<ModalStep>(
    centralizedModalStep || ModalStep.login
  );
  const [phoneMethod, setPhoneMethod] = useState<
    DefaultPhoneMethod | undefined | null
  >(defaultPhoneMethod);

  // Error states extracted to useState
  const [sendOtpError, setSendOtpError] = useState<string | null>(null);
  const [signupError, setSignupError] = useState<string | null>(null);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [existingOtpMessage, setExistingOtpMessage] = useState<string | null>(
    null
  );
  const [token, setToken] = useState<string>("");

  // Countdown state for retry after functionality
  const [countdown, setCountdown] = useState<number>(0);
  const [isCountdownActive, setIsCountdownActive] = useState<boolean>(false);

  // Form state management with useForm
  const {
    register,
    formState: { errors },
    trigger,
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
  } = useForm({
    defaultValues: {
      numOfResend: 0,
      otp: "",
      receiver: "",
      signupName: "",
      signupPhone: "",
      signupEmail: "",
      email: "",
      phonNumber: "",
      signupPhoneNumber: "",
    },
  });

  const [comingVerificationMethod, setComingVerificationMethod] =
    useState<VerificationMethod | null>(null);
  const [otpSessionId, setOtpSessionId] = useState<number>(0);

  // Memoized setter functions using setValue
  const setOtp = useCallback(
    (otp: string) => {
      setValue("otp", otp);
    },
    [setValue]
  );

  const setNumOfResend = useCallback(
    (numOfResend: number) => {
      setValue("numOfResend", numOfResend);
    },
    [setValue]
  );

  const setSignupName = useCallback(
    (signupName: string) => {
      setValue("signupName", signupName);
    },
    [setValue]
  );

  const setSignupEmail = useCallback(
    (signupEmail: string) => {
      setValue("signupEmail", signupEmail);
    },
    [setValue]
  );

  const setSignupPhone = useCallback(
    (signupPhone: string) => {
      setValue("signupPhone", signupPhone);
    },
    [setValue]
  );

  React.useEffect(() => {
    if (!isOpen) {
      setStep(centralizedModalStep || ModalStep.login);
      setMethod(getInitialMethod());
      // Reset form values
      reset({
        otp: "",
        receiver: "",
        signupName: "",
        signupPhone: "",
        signupEmail: "",
        numOfResend: 0,
        email: "",
        phonNumber: "",
        signupPhoneNumber: "",
      });
      // Reset error states
      setSendOtpError(null);
      setSignupError(null);
      setOtpError(null);
      setExistingOtpMessage(null);
      setPhoneMethod(defaultPhoneMethod);
      setComingVerificationMethod(null); // Reset verification method to default
      setOtpSessionId(0); // Reset OTP session ID
      // Reset countdown
      setCountdown(0);
      setIsCountdownActive(false);
    } else {
      // Set receiver to notVerifiedEmail or notVerifiedPhone if available
      if (notVerifiedEmail && hasEmailMethod) {
        setValue("receiver", notVerifiedEmail);
        setMethod(LoginMethod.EMAIL);
        setComingVerificationMethod(VerificationMethod.Email);
      } else if (notVerifiedPhone && hasPhoneMethod) {
        setMethod(LoginMethod.PHONE);
        setValue("receiver", notVerifiedPhone);
        if (defaultPhoneMethod === DefaultPhoneMethod.Sms) {
          setComingVerificationMethod(VerificationMethod.Sms);
        } else {
          setComingVerificationMethod(VerificationMethod.Whatsapp);
        }
      } else {
        // Set method based on available verification methods
        setMethod(getInitialMethod());
      }
      // Reset form when modal opens
      reset();
    }
  }, [
    isOpen,
    defaultPhoneMethod,
    reset,
    setValue,
    notVerifiedEmail,
    notVerifiedPhone,
    centralizedModalStep,
    hasEmailMethod,
    hasPhoneMethod,
    getInitialMethod,
  ]);

  const intl = useIntl();
  const [sendOtp, { loading: isLoading }] = useSendCustomerOtpMutation();
  const ButtonColorsOnType = getButtonColorsOnType({
    storePrimaryColor: appearance?.colors?.primary || theme.colors.primary,
  });
  const PrimaryButtonColor = ButtonColorsOnType["primary"];
  const cachedCountry = isBrowser
    ? (localStorage.getItem("default-country") as string) || undefined
    : undefined;

  // Countdown timer effect
  React.useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isCountdownActive && countdown > 0) {
      timer = setTimeout(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    } else if (countdown === 0) {
      setIsCountdownActive(false);
    }
    return () => clearTimeout(timer);
  }, [countdown, isCountdownActive]);

  const getAlternativeMethod = useCallback((): VerificationMethod | null => {
    if (method === LoginMethod.EMAIL) {
      return null;
    }

    const currentMethod =
      phoneMethod === DefaultPhoneMethod.Whatsapp
        ? VerificationMethod.Whatsapp
        : VerificationMethod.Sms;

    // Check which phone methods are available
    const availableMethods = verificationMethods || [];
    const hasSms = availableMethods.includes(VerificationMethod.Sms);
    const hasWhatsapp = availableMethods.includes(VerificationMethod.Whatsapp);

    // Return alternative only if it's available
    if (currentMethod === VerificationMethod.Sms && hasWhatsapp) {
      return VerificationMethod.Whatsapp;
    }
    if (currentMethod === VerificationMethod.Whatsapp && hasSms) {
      return VerificationMethod.Sms;
    }

    return null;
  }, [method, phoneMethod, verificationMethods]);

  const onSubmit = useCallback(
    async (values: any) => {
      setSendOtpError(null);
      setExistingOtpMessage(null);
      if (
        (values?.email && !errors.email) ||
        (values?.phonNumber && !errors.phonNumber)
      ) {
        // Determine the correct field and verification method based on current method
        const isEmailMethod = method === LoginMethod.EMAIL;
        const currentFieldValue = isEmailMethod
          ? values?.email
          : values?.phonNumber;
        const currentFieldError = isEmailMethod
          ? errors.email
          : errors.phonNumber;

        try {
          setValue("receiver", currentFieldValue);
          const verificationMethod =
            comingVerificationMethod ??
            (isEmailMethod
              ? VerificationMethod.Email
              : phoneMethod === DefaultPhoneMethod.Whatsapp
                ? VerificationMethod.Whatsapp
                : VerificationMethod.Sms);

          const result = await sendOtp({
            variables: {
              input: {
                receiver:
                  notVerifiedEmail || notVerifiedPhone || currentFieldValue,
                verificationMethod,
                storeId: storeId || "",
              },
            },
          });

          if (result.data?.sendCustomerOTP?.success) {
            if (result.data?.sendCustomerOTP?.existingOtp) {
              // Show existing OTP message for resend
              setExistingOtpMessage(
                intl.formatMessage({
                  defaultMessage: "Your OTP is still active, please fill it",
                  id: "4lMdpU",
                })
              );
            }
            setStep(ModalStep.otp);
            setOtpSessionId((prev) => prev + 1); // Increment to reset countdown
          } else {
            const errorCode = result.data?.sendCustomerOTP?.code;
            let formattedError = intl.formatMessage({
              defaultMessage: "Failed to send OTP. Please try again.",
              id: "Ixaksk",
            });

            if (errorCode) {
              try {
                formattedError =
                  getStoreAuthErrorMessageString(errorCode, intl) ||
                  getCustomerErrorMessageString(errorCode, intl) ||
                  errorCode;
              } catch {
                formattedError = errorCode;
              }
            }

            setSendOtpError(formattedError);
          }
        } catch (err: any) {
          let errorCode = intl.formatMessage({
            defaultMessage: "Failed to send OTP. Please try again.",
            id: "Ixaksk",
          });

          const error = err?.graphQLErrors?.[0];
          const responseJson = error?.extensions;
          const retryAfter = responseJson?.responseJson;

          // Handle retry after countdown - try multiple possible paths
          let retryAfterValue = retryAfter;
          if (!retryAfterValue) {
            // Try alternative paths for retryAfter
            retryAfterValue =
              responseJson?.retryAfter ||
              error?.retryAfter ||
              responseJson?.retry_after ||
              error?.extensions?.retryAfter;
          }

          if (
            retryAfterValue &&
            typeof retryAfterValue === "number" &&
            retryAfterValue > 1
          ) {
            setCountdown(retryAfterValue);
            setIsCountdownActive(true);
          }

          if (error) {
            // Try to parse as store auth error first
            try {
              errorCode =
                getStoreAuthErrorMessageString(
                  error.errorCode || error.message,
                  intl
                ) ||
                getCustomerErrorMessageString(
                  error.errorCode || error.message,
                  intl
                ) ||
                error.message ||
                errorCode;
            } catch {
              // If not a store auth error, use the original message or fallback
              errorCode =
                getStoreAuthErrorMessageString(
                  error.errorCode || error.message,
                  intl
                ) ||
                getCustomerErrorMessageString(
                  error.errorCode || error.message,
                  intl
                ) ||
                error.message;
            }
          }

          setSendOtpError(errorCode);

          // Check for rate limiting after error message is set
          if (
            !retryAfterValue &&
            (errorCode.includes("Rate limit exceeded") ||
              errorCode.includes("try again later") ||
              errorCode.includes("rate limit") ||
              error?.message?.includes("rate limit") ||
              error?.message?.includes("Rate limit"))
          ) {
            // Fallback: if we detect rate limiting but no retryAfter value, use default countdown

            setCountdown(60);
            setIsCountdownActive(true);
          }
        }
      }
    },
    [
      errors.email,
      errors.phonNumber,
      comingVerificationMethod,
      method,
      phoneMethod,
      notVerifiedEmail,
      notVerifiedPhone,
      storeId,
      sendOtp,
      intl,
      setValue,
    ]
  );

  const handleVerifyOTP = useCallback(async () => {
    const currentOtp = watch("otp");
    const currentReceiver = watch("receiver");

    if (!currentOtp || currentOtp.length < 4) {
      setOtpError(
        intl.formatMessage({
          defaultMessage: "Please enter a valid OTP",
          id: "ijRjKo",
        })
      );
      return;
    }

    setOtpError(null);

    try {
      const verificationMethod =
        comingVerificationMethod ??
        (method === LoginMethod.EMAIL
          ? VerificationMethod.Email
          : phoneMethod === DefaultPhoneMethod.Whatsapp
            ? VerificationMethod.Whatsapp
            : VerificationMethod.Sms);

      const response = await verifyOTP({
        verificationMethod: verificationMethod,
        receiver: currentReceiver,
        storeId: storeId || "",
        otp: currentOtp,
      });

      if (response.success) {
        if (response.requiresSignup) {
          if (method === LoginMethod.EMAIL) {
            setValue("signupEmail", currentReceiver);
          } else {
            setValue("signupPhone", currentReceiver);
          }
          setStep(ModalStep.signup);
          setToken(response?.token || "");
        } else {
          try {
            const decodedPayload = decodeToken(response?.token || "");
            handleLoginUpdate({
              loginResponseData: decodedPayload,
              token: response?.token,
              updateUser,
            });
            await updateContactInfo({
              info: {
                name: decodedPayload?.name!,
                email: decodedPayload?.email!,
                phone: decodedPayload?.phone!,
              },
            });
            onClose();
            onAuthSuccess?.();
          } catch (loginError: any) {
            let errorMsg = intl.formatMessage({
              defaultMessage: "Login failed. Please try again.",
              id: "hAgYFt",
            });

            if (loginError) {
              try {
                errorMsg =
                  getStoreAuthErrorMessageString(
                    loginError.code || loginError.message,
                    intl
                  ) ||
                  getCustomerErrorMessageString(
                    loginError.code || loginError.message,
                    intl
                  ) ||
                  loginError.message ||
                  errorMsg;
              } catch (error) {
                errorMsg =
                  getStoreAuthErrorMessageString(
                    error.code || error.message,
                    intl
                  ) ||
                  getCustomerErrorMessageString(
                    error.code || error.message,
                    intl
                  ) ||
                  error.message;
              }
            }
            setOtpError(errorMsg);
          }
        }
      } else {
        setOtpError(
          getStoreAuthErrorMessageString(response.message, intl) ||
            getCustomerErrorMessageString(response.message, intl) ||
            response.message ||
            intl.formatMessage({
              defaultMessage: "OTP verification failed. Please try again.",
              id: "dGqYdS",
            })
        );
      }
    } catch (error: any) {
      let errorMsg = intl.formatMessage({
        defaultMessage: "OTP verification failed. Please try again.",
        id: "dGqYdS",
      });

      if (error) {
        try {
          errorMsg =
            getStoreAuthErrorMessageString(error.code || error.message, intl) ||
            getCustomerErrorMessageString(error.code || error.message, intl) ||
            error.message ||
            errorMsg;
        } catch {
          errorMsg = error.message || errorMsg;
        }
      }
      setOtpError(errorMsg);
    }
  }, [
    watch,
    intl,
    comingVerificationMethod,
    method,
    phoneMethod,
    storeId,
    setValue,
    updateContactInfo,
    updateUser,
    onClose,
    onAuthSuccess,
  ]);

  const handleResendOTP = useCallback(
    async (overrideVerificationMethod?: VerificationMethod) => {
      setOtpError(null);
      setExistingOtpMessage(null);

      // Use the override method if provided, otherwise use current verification method
      const verificationMethodToUse =
        (overrideVerificationMethod || comingVerificationMethod) ??
        (method === LoginMethod.EMAIL
          ? VerificationMethod.Email
          : phoneMethod === DefaultPhoneMethod.Whatsapp
            ? VerificationMethod.Whatsapp
            : VerificationMethod.Sms);

      // Update the state if using override method
      if (overrideVerificationMethod) {
        setComingVerificationMethod(overrideVerificationMethod);
      }
      try {
        const currentReceiver = watch("receiver");
        const result = await sendOtp({
          variables: {
            input: {
              receiver: notVerifiedEmail || notVerifiedPhone || currentReceiver,
              verificationMethod: verificationMethodToUse,
              storeId: storeId || "",
            },
          },
        });

        if (result.data?.sendCustomerOTP?.success) {
          if (result.data?.sendCustomerOTP?.existingOtp) {
            // Show existing OTP message for resend
            setExistingOtpMessage(
              intl.formatMessage({
                defaultMessage: "Your OTP is still active, please fill it",
                id: "4lMdpU",
              })
            );
          }
          setValue("otp", "");
        } else {
          const errorCode = result.data?.sendCustomerOTP?.code;
          let formattedError = intl.formatMessage({
            defaultMessage: "Failed to resend OTP. Please try again.",
            id: "SqwqeG",
          });

          if (errorCode) {
            try {
              formattedError =
                getStoreAuthErrorMessageString(errorCode, intl) ||
                getCustomerErrorMessageString(errorCode, intl) ||
                errorCode;
            } catch {
              formattedError = errorCode;
            }
          }

          setOtpError(formattedError);
        }
      } catch (error: any) {
        let errorCode = intl.formatMessage({
          defaultMessage: "Failed to resend OTP. Please try again.",
          id: "SqwqeG",
        });

        if (error) {
          try {
            errorCode =
              getStoreAuthErrorMessageString(
                error.code || error.message,
                intl
              ) ||
              getCustomerErrorMessageString(
                error.code || error.message,
                intl
              ) ||
              error.message ||
              errorCode;
          } catch {
            errorCode = error.message || errorCode;
          }
        }

        setOtpError(errorCode);
      }
    },
    [
      watch,
      comingVerificationMethod,
      method,
      phoneMethod,
      storeId,
      sendOtp,
      intl,
      setValue,
      setExistingOtpMessage,
    ]
  );

  const handleSignup = useCallback(async () => {
    setSignupError(null);

    // Get phone value from form if method is Email
    const currentSignupPhone = watch("signupPhone");
    const currentSignupEmail = watch("signupEmail");
    let phoneValue = currentSignupPhone;
    const emailValue = currentSignupEmail;

    if (method === LoginMethod.EMAIL) {
      const formPhone = watch("signupPhoneNumber");
      phoneValue = formPhone || currentSignupPhone;
    }

    const currentSignupName = watch("signupName");

    // Updated validation logic to account for available verification methods
    const isNameMissing = !currentSignupName;
    const isPhoneMissing =
      method === LoginMethod.EMAIL && hasPhoneMethod && !phoneValue;
    const isEmailMissing =
      method === LoginMethod.PHONE && hasEmailMethod && !emailValue;

    if (isNameMissing || isPhoneMissing || isEmailMissing) {
      setSignupError(
        intl.formatMessage({
          defaultMessage: "Please fill in all required fields",
          id: "gZz74a",
        })
      );
      return;
    }

    try {
      const currentReceiver = watch("receiver");
      const signupData = {
        name: currentSignupName,
        email: method === LoginMethod.EMAIL ? currentReceiver : emailValue,
        phone: method === LoginMethod.PHONE ? currentReceiver : phoneValue,
        storeId: storeId || "",
      };

      const response = await signup(signupData, {}, token);

      if (response.success) {
        if (response.data) {
          handleLoginUpdate({
            loginResponseData: response.data,
            token: response?.token,
            updateUser,
          });
        }
        onClose();

        await updateContactInfo({
          info: {
            name: response.data?.name!,
            email: response.data?.email!,
            phone: response.data?.phone!,
          },
        });
        onAuthSuccess?.();
      } else {
        const errorMsg =
          getCustomerErrorMessageString(response.code, intl) ||
          getStoreAuthErrorMessageString(response.code, intl) ||
          response.message ||
          intl.formatMessage({
            defaultMessage: "Signup failed. Please try again.",
            id: "Inq0ry",
          });

        setSignupError(errorMsg);
      }
    } catch (error: any) {
      let errorMsg = "";

      if (error) {
        // Handle specific duplicate errors with linkedData from error object
        if (
          error.code === CustomerServiceErrorCodes.DuplicateEmail &&
          error.linkedData
        ) {
          errorMsg = intl.formatMessage(
            {
              defaultMessage:
                "This email is already used by phone {linkedData}",
              id: "mo2XVp",
            },
            { linkedData: error.linkedData }
          );
        } else if (
          error.code === CustomerServiceErrorCodes.DuplicatePhone &&
          error.linkedData
        ) {
          errorMsg = intl.formatMessage(
            {
              defaultMessage:
                "This phone is already used by email {linkedData}",
              id: "g20BVR",
            },
            { linkedData: error.linkedData }
          );
        } else {
          // Fallback for other error codes or when conditions aren't met
          errorMsg =
            getStoreAuthErrorMessageString(error.code, intl) ||
            getCustomerErrorMessageString(error.code, intl) ||
            error.message;
        }
      } else {
        errorMsg = intl.formatMessage({
          defaultMessage: "Signup failed. Please try again.",
          id: "Inq0ry",
        });
      }

      setSignupError(errorMsg);
    }
  }, [
    watch,
    method,
    hasPhoneMethod,
    hasEmailMethod,
    intl,
    storeId,
    onClose,
    updateContactInfo,
    onAuthSuccess,
    updateUser,
  ]);

  // Handler functions for child components
  const handleSwitchMethod = useCallback(() => {
    // Determine current verification method
    const currentVerificationMethod =
      comingVerificationMethod ??
      (phoneMethod === DefaultPhoneMethod.Whatsapp
        ? VerificationMethod.Whatsapp
        : VerificationMethod.Sms);

    // Switch to the alternative method
    const newVerificationMethod =
      currentVerificationMethod === VerificationMethod.Sms
        ? VerificationMethod.Whatsapp
        : VerificationMethod.Sms;

    // Update phoneMethod to match the new verification method
    const newPhoneMethod =
      newVerificationMethod === VerificationMethod.Whatsapp
        ? DefaultPhoneMethod.Whatsapp
        : DefaultPhoneMethod.Sms;

    setPhoneMethod(newPhoneMethod);
    setValue("numOfResend", 0);
    setOtpSessionId((prev) => prev + 1); // Reset countdown component

    // Pass the new verification method directly to avoid state timing issues
    // The comingVerificationMethod will be updated inside handleResendOTP
    handleResendOTP(newVerificationMethod);
  }, [phoneMethod, comingVerificationMethod, setValue, handleResendOTP]);

  const handleMethodChange = useCallback((newMethod: LoginMethod) => {
    setMethod(newMethod);
    // Reset countdown when switching between email and phone methods
    setCountdown(0);
    setIsCountdownActive(false);
  }, []);

  const handleBackToLogin = useCallback(() => {
    setStep(ModalStep.login);
    setMethod(getInitialMethod());
    reset({
      otp: "",
      numOfResend: 0,
      receiver: "",
      signupName: "",
      signupPhone: "",
      signupEmail: "",
      email: "",
      phonNumber: "",
      signupPhoneNumber: "",
    });
    // Reset error states
    setSendOtpError(null);
    setSignupError(null);
    setOtpError(null);
    setExistingOtpMessage(null);
    // Reset verification method to default when going back to login
    setComingVerificationMethod(null);
  }, [reset, getInitialMethod]);

  return (
    <>
      <Modal
        show={isOpen}
        onClose={onClose}
        overLayColor="rgba(0,0,0,.8)"
        className="!w-[95vw] sm:!w-[500px] !rounded-xl !max-h-[90vh] !overflow-y-auto"
      >
        <Modal.Body>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="flex flex-col justify-start items-start gap-6">
              <ModalHeader
                step={step}
                method={method}
                phoneMethod={phoneMethod}
                receiver={watch("receiver") || ""}
                watchValue={watch}
                notVerifiedEmail={notVerifiedEmail}
                notVerifiedPhone={notVerifiedPhone}
              />

              {step === ModalStep.login && hasEmailMethod && hasPhoneMethod && (
                <LoginSwitcher
                  activeTab={method}
                  setActiveTab={handleMethodChange}
                />
              )}

              {step === ModalStep.login &&
                (hasEmailMethod || hasPhoneMethod) && (
                  <LoginForm
                    method={method}
                    register={register}
                    errors={errors}
                    trigger={trigger}
                    control={control}
                    cachedCountry={cachedCountry || undefined}
                  />
                )}

              {sendOtpError && (
                <div className="flex  w-full justify-start items-center gap-2 !bg-error-25 p-2 !rounded-md border border-error-300">
                  <FeaturedOutlineIcon className="!text-error-600" />
                  <span className="text-error-700 text-sm">{sendOtpError}</span>
                </div>
              )}

              {step === ModalStep.otp && (
                <OtpVerification
                  key={`otp-${otpSessionId}`} // Reset component when starting new OTP session
                  otp={watch("otp") || ""}
                  setOtp={setOtp}
                  primaryButtonColor={PrimaryButtonColor}
                  method={method}
                  phoneMethod={phoneMethod}
                  numOfResend={watch("numOfResend") || 0}
                  setNumOfResend={setNumOfResend}
                  isLoading={isLoading}
                  otpError={otpError}
                  onResendOTP={handleResendOTP}
                  onSwitchMethod={handleSwitchMethod}
                  getAlternativeMethod={getAlternativeMethod}
                />
              )}
              {existingOtpMessage && (
                <div className="flex  w-full justify-start items-center gap-2 !bg-red-25 p-2 !rounded-md border border-red-300">
                  <FeaturedOutlineIcon className="!text-red-600" />
                  <span className="text-red-700 text-sm">
                    {existingOtpMessage}
                  </span>
                </div>
              )}
              {step === ModalStep.signup && (
                <SignupForm
                  method={method}
                  setSignupName={setSignupName}
                  setSignupEmail={setSignupEmail}
                  signupPhone={watch("signupPhone")}
                  setSignupPhone={setSignupPhone}
                  control={control}
                  register={register}
                  errors={errors}
                  cachedCountry={cachedCountry || undefined}
                />
              )}

              {signupError && (
                <div className="flex  w-full justify-start items-center gap-2 !bg-error-25 p-2 !rounded-md border border-error-300">
                  <FeaturedOutlineIcon className="!text-error-600" />
                  <span className="text-error-700 text-sm">{signupError}</span>
                </div>
              )}

              <ActionButtons
                step={step}
                method={method}
                isLoading={isLoading}
                otp={watch("otp") || ""}
                signupName={watch("signupName") || ""}
                signupEmail={watch("signupEmail") || ""}
                watchValue={watch}
                onBackToLogin={handleBackToLogin}
                onVerifyOTP={handleVerifyOTP}
                onSignup={handleSignup}
                errors={errors}
                countdown={countdown}
                isCountdownActive={isCountdownActive}
              />
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default CentralizedModal;
